import { Link, getRouteApi } from '@tanstack/react-router';
import { Suspense, lazy, useEffect, useRef, useState } from 'react';
import { AUDIENCE_NAMES, type EntryDetail, type EntryLinkView } from '@realmery/realm-core';
import { formatDate } from '../files.ts';
import { EntryContent, LockedContent } from './EntryContent.tsx';
import { ImageGallery } from './EntryImages.tsx';
import { findRanges, showHighlight } from './findHighlight.ts';
import { options } from './SearchPage.tsx';
import { EntrySheet } from './sheets/EntrySheet.tsx';

const route = getRouteApi('/_app/realm/$realmKey/entry/$entryId');
const realmRoute = getRouteApi('/_app/realm/$realmKey');

// The editor (ProseMirror) is fetched only when an entry is first edited.
const EntryEditor = lazy(() => import('./EntryEditor.tsx').then((m) => ({ default: m.EntryEditor })));

export function EntryPage() {
  const entry = route.useLoaderData();
  // A realm shared with this account is read-only: it opens to read, never in the editor.
  const readOnly = realmRoute.useLoaderData().readOnly;
  const edit = route.useSearch().edit && !readOnly;
  if (!entry) {
    return (
      <article className="document">
        <h1>Entry not found</h1>
        <p>It may have been deleted.</p>
      </article>
    );
  }
  if (edit) {
    return (
      <Suspense fallback={<p className="meta">Opening the editor…</p>}>
        <EntryEditor key={entry.id} entry={entry} />
      </Suspense>
    );
  }
  return <EntryView key={entry.id} entry={entry} />;
}

function EntryView({ entry }: { entry: EntryDetail }) {
  const { realmKey } = route.useParams();
  const readOnly = realmRoute.useLoaderData().readOnly;
  const { find, case: matchCase = false, words: wholeWords = false } = route.useSearch();
  const audience = AUDIENCE_NAMES[entry.audience.role] ?? entry.audience.role;
  const article = useRef<HTMLElement>(null);
  const [found, setFound] = useState<number | null>(null);

  // A search's matches (`?find=`), marked in the title, labels, aliases, fields and text, and scrolled to.
  useEffect(() => {
    if (!find || !article.current) {
      setFound(null);
      return;
    }
    const ranges = findRanges(article.current.querySelectorAll('[data-find]'), { text: find, matchCase, wholeWords });
    setFound(ranges.length);
    const clear = showHighlight(ranges);
    ranges[0]?.startContainer.parentElement?.scrollIntoView({ block: 'center' });
    return clear;
  }, [entry, find, matchCase, wholeWords]);

  return (
    <article className="document" ref={article}>
      {find && found !== null && (
        <p className="find-bar" role="status">
          {found === 0 ? `No matches for “${find}” in what's shown here.` : `${found} ${found === 1 ? 'match' : 'matches'} for “${find}” highlighted.`}{' '}
          <Link to="/realm/$realmKey/search" params={{ realmKey }} search={{ q: find, ...options(matchCase, wholeWords) }}>
            Back to the results
          </Link>
          {' · '}
          <Link to="/realm/$realmKey/entry/$entryId" params={{ realmKey, entryId: entry.id }} search={{}}>
            Clear
          </Link>
        </p>
      )}
      <div className="entry-head">
        <p className="eyebrow">{entry.typeName}</p>
        <div className="actions">
          <Link className="button quiet" to="/realm/$realmKey/graph" params={{ realmKey }} search={{ view: 'around', entry: entry.id }}>
            Show in graph
          </Link>
          {!readOnly && (
            <Link className="button" to="/realm/$realmKey/entry/$entryId" params={{ realmKey, entryId: entry.id }} search={{ edit: true }}>
              Edit
            </Link>
          )}
        </div>
      </div>
      <h1 data-find="">{entry.title}</h1>
      <div className="chips">
        <span className="chip" title="Who this entry is for">
          {entry.audience.role === 'CUSTOM' && entry.audience.customLabel ? entry.audience.customLabel : audience}
        </span>
        {entry.labels.map((label) => (
          <span key={label} className="chip chip-label" data-find="">
            {label}
          </span>
        ))}
      </div>
      {entry.aliases.length > 0 && (
        <p className="meta">
          Also known as <span data-find="">{entry.aliases.join(', ')}</span>
        </p>
      )}

      {entry.metadata.length > 0 && (
        <dl className="fields">
          {entry.metadata.map(([key, value]) => (
            <div key={key}>
              <dt data-find="">{key}</dt>
              <dd data-find="">{value}</dd>
            </div>
          ))}
        </dl>
      )}

      {entry.locked ? <LockedContent blob={entry.encryptedContent} /> : <EntryContent html={entry.contentHtml} />}

      {entry.images.length > 0 && (
        <section>
          <h2>Images</h2>
          <ImageGallery images={entry.images} />
        </section>
      )}
      <EntrySheet realmKey={realmKey} entry={entry} />
      <LinkList heading="Relationships" links={entry.links} />
      <LinkList heading="Linked from" links={entry.backlinks} />
      {entry.collections.length > 0 && (
        <section>
          <h2>Collections</h2>
          <p>{entry.collections.join(', ')}</p>
        </section>
      )}
      <p className="meta">
        Created {formatDate(entry.created)} · edited {formatDate(entry.modified)}
      </p>
    </article>
  );
}

function LinkList({ heading, links }: { heading: string; links: readonly EntryLinkView[] }) {
  const { realmKey } = route.useParams();
  if (links.length === 0) return null;
  return (
    <section>
      <h2>{heading}</h2>
      <ul className="link-list">
        {links.map((link) => (
          <li key={`${link.relationshipType}:${link.entryId}`}>
            <span className="relationship">{link.relationshipType}</span>{' '}
            <Link to="/realm/$realmKey/entry/$entryId" params={{ realmKey, entryId: link.entryId }}>
              {link.title}
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

